import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, cn } from '../ui/Kit';
import Spinner from '../ui/Spinner';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../lib/api';
import { useToast } from '../ui/Toast';
import { CHANNEL_LABELS, LEAD_TYPE_LABELS } from '../../lib/leadMeta';

const { FiX } = FiIcons;

const STATUS_LABELS = {
  new: 'New',
  contacted: 'Contacted',
  follow_up: 'Follow Up',
  appointment_set: 'Appointment Set',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
  dnc: 'Do Not Contact',
};

const MERGE_FIELDS = [
  { key: 'first_name', label: 'First Name' },
  { key: 'last_name', label: 'Last Name' },
  { key: 'phone', label: 'Phone' },
  { key: 'phone_secondary', label: 'Phone Secondary' },
  { key: 'email', label: 'Email' },
  { key: 'lead_type', label: 'Lead Type' },
  { key: 'lead_source', label: 'Lead Source' },
  { key: 'status', label: 'Status' },
  { key: 'channel', label: 'Channel' },
  { key: 'address', label: 'Address' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zip', label: 'Zip' },
  { key: 'dob', label: 'Date of Birth' },
  { key: 'gender', label: 'Gender' },
  { key: 'notes', label: 'Notes' },
];

function leadFullName(lead) {
  return [lead?.first_name, lead?.last_name].filter(Boolean).join(' ') || 'Unnamed lead';
}

function fieldRaw(lead, key) {
  const v = lead?.[key];
  if (v === null || v === undefined) return '';
  return String(v).trim();
}

function formatDisplayValue(lead, key) {
  const raw = fieldRaw(lead, key);
  if (!raw) return '—';
  if (key === 'lead_type') return LEAD_TYPE_LABELS[raw] || raw;
  if (key === 'channel') return CHANNEL_LABELS[raw] || raw;
  if (key === 'status') return STATUS_LABELS[raw] || raw;
  if (key === 'notes' && raw.length > 80) return `${raw.slice(0, 80)}…`;
  return raw;
}

function buildInitialChoices(leadA, leadB) {
  const choices = {};
  for (const { key } of MERGE_FIELDS) {
    const a = fieldRaw(leadA, key);
    const b = fieldRaw(leadB, key);
    if (a === b) choices[key] = 'A';
    else if (a && !b) choices[key] = 'A';
    else if (!a && b) choices[key] = 'B';
    else choices[key] = 'A';
  }
  return choices;
}

function resolveMergedValue(leadA, leadB, key, choice) {
  return choice === 'B' ? leadB?.[key] : leadA?.[key];
}

function FieldChoiceRow({ field, leadA, leadB, choice, onSelect }) {
  const { key, label } = field;
  const aRaw = fieldRaw(leadA, key);
  const bRaw = fieldRaw(leadB, key);
  const identical = aRaw === bRaw;
  const onlyA = !!aRaw && !bRaw;
  const onlyB = !aRaw && !!bRaw;

  const selectA = () => {
    if (!onlyB) onSelect(key, 'A');
  };
  const selectB = () => {
    if (!onlyA) onSelect(key, 'B');
  };

  const cellClass = (side, disabled) =>
    cn(
      'flex-1 min-w-0 px-3 py-2 rounded-lg border text-sm transition-colors',
      disabled && 'opacity-40 cursor-not-allowed bg-gray-50 border-gray-100 text-gray-400',
      !disabled && 'cursor-pointer',
      !disabled && choice === side && 'border-blue-500 bg-blue-50 text-blue-900 ring-1 ring-blue-500',
      !disabled && choice !== side && 'border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:bg-blue-50/40',
    );

  return (
    <div className="grid grid-cols-[140px_1fr_1fr] gap-3 items-start py-2 border-b border-gray-100 last:border-0">
      <div className="text-xs font-medium text-gray-500 pt-2">{label}</div>
      {identical ? (
        <div className="col-span-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-800">
          {formatDisplayValue(leadA, key)}
          <span className="ml-2 text-xs text-gray-400">(same on both)</span>
        </div>
      ) : (
        <>
          <button type="button" className={cellClass('A', onlyB)} onClick={selectA} disabled={onlyB}>
            {formatDisplayValue(leadA, key)}
          </button>
          <button type="button" className={cellClass('B', onlyA)} onClick={selectB} disabled={onlyA}>
            {formatDisplayValue(leadB, key)}
          </button>
        </>
      )}
    </div>
  );
}

export default function MergeLeadsModal({
  isOpen,
  onClose,
  leadA,
  leadB,
  agencyUsers: _agencyUsers,
  onMerged,
}) {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [choices, setChoices] = useState({});
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    if (!isOpen || !leadA || !leadB) return;
    setChoices(buildInitialChoices(leadA, leadB));
  }, [isOpen, leadA, leadB]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape' && !merging) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, merging, onClose]);

  const handleSelect = useCallback((key, side) => {
    setChoices((prev) => ({ ...prev, [key]: side }));
  }, []);

  const previewLead = useMemo(() => {
    if (!leadA || !leadB) return null;
    const out = {};
    for (const { key } of MERGE_FIELDS) {
      out[key] = resolveMergedValue(leadA, leadB, key, choices[key] || 'A');
    }
    return out;
  }, [leadA, leadB, choices]);

  const handleMerge = async () => {
    if (!leadA?.id || !leadB?.id || !user?.id || !user?.agency_id) return;

    const countA = MERGE_FIELDS.filter((f) => choices[f.key] === 'A').length;
    const countB = MERGE_FIELDS.filter((f) => choices[f.key] === 'B').length;
    const leadASurvives = countA >= countB;

    const survivingId = leadASurvives ? leadA.id : leadB.id;
    const absorbedId = leadASurvives ? leadB.id : leadA.id;

    const fieldChoices = {};
    for (const { key } of MERGE_FIELDS) {
      const uiChoice = choices[key] || 'A';
      fieldChoices[key] = leadASurvives ? uiChoice : (uiChoice === 'A' ? 'B' : 'A');
    }

    setMerging(true);
    try {
      const survivingLead = await api.leads.merge({
        survivingId,
        absorbedId,
        fieldChoices,
        userId: user.id,
        agencyId: user.agency_id,
      });
      addToast({ type: 'success', title: 'Leads merged successfully' });
      onMerged?.(survivingLead);
      onClose();
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Merge failed',
        message: err?.message || 'Could not merge leads',
      });
    } finally {
      setMerging(false);
    }
  };

  if (!isOpen || !leadA || !leadB) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={merging ? undefined : onClose} aria-hidden />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="merge-leads-title"
        className="relative z-10 flex flex-col w-[80vw] max-w-[900px] max-h-[90vh] bg-white rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-gray-200 rounded-t-xl">
          <h2 id="merge-leads-title" className="text-lg font-semibold text-gray-900">
            Merge Leads
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={merging}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="grid grid-cols-[140px_1fr_1fr] gap-3 mb-2">
            <div />
            <div className="text-sm font-semibold text-gray-900">
              Lead A — {leadFullName(leadA)}
            </div>
            <div className="text-sm font-semibold text-gray-900">
              Lead B — {leadFullName(leadB)}
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 px-3 py-1 mb-6">
            {MERGE_FIELDS.map((field) => (
              <FieldChoiceRow
                key={field.key}
                field={field}
                leadA={leadA}
                leadB={leadB}
                choice={choices[field.key] || 'A'}
                onSelect={handleSelect}
              />
            ))}
          </div>

          <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-4">
            <h3 className="text-sm font-semibold text-blue-900 mb-3">Surviving Record Preview</h3>
            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-sm">
              {MERGE_FIELDS.map(({ key, label }) => (
                <div key={key}>
                  <dt className="text-xs text-gray-500">{label}</dt>
                  <dd className="font-medium text-gray-900 truncate">
                    {formatDisplayValue(previewLead, key)}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-6 py-4 border-t border-gray-200 rounded-b-xl bg-gray-50">
          <p className="text-xs text-gray-500 max-w-md">
            The non-surviving lead will be archived and its history merged into the surviving record.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={merging}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleMerge} disabled={merging} isLoading={merging}>
              Merge Leads
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
